import { useI18n, useL } from "../i18n";
import { useTrip } from "../TripContext";
import { Trash2, MapPin, Hotel, UtensilsCrossed, Minus, Plus, Save, Share2, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { saveItinerary } from "../api";
import { toast } from "sonner";
import { useState } from "react";
import SocialShare from "./SocialShare";

export default function TripPlanner() {
  const { stops, removeStop, updateStop, clearTrip, totalUsd, totalDays } = useTrip();
  const { t, lang } = useI18n();
  const L = useL();
  const [sharedId, setSharedId] = useState(null);

  const handleSave = async () => {
    if (stops.length === 0) return;
    try {
      const res = await saveItinerary({
        title: L({ ar: "رحلتي إلى الشرق الأوسط", en: "My Middle East trip", fr: "Mon voyage au Moyen-Orient" }),
        days: totalDays,
        stops: stops.map((s) => ({
          destination_id: s.destination.id,
          day: s.day,
          hotel_tier: s.hotel_tier,
          meals_tier: s.meals_tier,
          include_ticket: s.include_ticket,
        })),
        total_usd: totalUsd,
      });
      setSharedId(res.id);
      toast.success(L({
        ar: "تم حفظ الرحلة — شاركها الآن ✓",
        en: "Trip saved — share it now ✓",
        fr: "Voyage enregistré — partagez-le ✓"
      }));
    } catch {
      toast.error(L({ ar: "تعذّر حفظ الرحلة", en: "Failed to save trip", fr: "Échec de l'enregistrement" }));
    }
  };

  const shareUrl = sharedId ? `${window.location.origin}/trip/${sharedId}` : null;
  const shareText = L({
    ar: `شاهد رحلتي إلى الشرق الأوسط على رحّال — ${totalDays} ليلة، تكلفة تقديرية $${Math.round(totalUsd)}`,
    en: `Check out my Middle East trip on Rahhal — ${totalDays} nights, est. $${Math.round(totalUsd)}`,
    fr: `Découvrez mon voyage au Moyen-Orient sur Rahhal — ${totalDays} nuits, env. $${Math.round(totalUsd)}`
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="trip-planner">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C85A32] font-semibold">{t("nav_planner")}</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#1C1917]">
            {L({ ar: "خطط رحلتك بذكاء", en: "Plan your trip smartly", fr: "Planifiez votre voyage intelligemment" })}
          </h1>
        </div>
        {stops.length > 0 && (
          <button
            onClick={clearTrip}
            className="text-sm text-[#78716C] hover:text-[#C85A32] transition-colors self-start md:self-auto"
            data-testid="clear-trip"
          >
            {L({ ar: "مسح الرحلة", en: "Clear trip", fr: "Effacer" })}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {stops.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E6DED3] p-12 text-center">
              <MapPin className="h-10 w-10 text-[#78716C] mx-auto" />
              <p className="mt-3 text-[#57534E]">{t("empty_trip")}</p>
              <Link
                to="/"
                className="mt-5 inline-flex px-5 py-2.5 rounded-full bg-[#1C1917] text-[#FAF7F2] hover:bg-[#C85A32] transition-colors text-sm"
              >
                {t("hero_cta_explore")}
              </Link>
            </div>
          ) : (
            stops.map((stop) => <StopCard key={stop.destination.id} stop={stop} onRemove={removeStop} onUpdate={updateStop} />)
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-3xl bg-[#1C1917] text-[#FAF7F2] p-6 space-y-5">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70">{t("total_cost")}</div>
              <div className="font-display text-5xl mt-2 flex items-baseline gap-1">
                <span className="text-xl opacity-80">$</span>
                {totalUsd.toFixed(0)}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {totalDays} {L({ ar: "ليلة", en: "nights", fr: "nuits" })} · {stops.length} {L({ ar: "وجهة", en: "stops", fr: "étapes" })}
              </div>
            </div>

            <div className="pt-4 border-t border-white/10 space-y-2 text-sm">
              {stops.map((s) => {
                const p = s.destination.prices;
                const perNight = (p[`hotel_${s.hotel_tier}_usd`] || 0) + (p[`meal_${s.meals_tier}_usd`] || 0) * 3 + (p.transport_daily_usd || 0);
                const sub = perNight * s.nights + (s.include_ticket ? (p.ticket_usd || 0) : 0);
                return (
                  <div key={s.destination.id} className="flex justify-between opacity-90">
                    <span className="truncate">{lang === "ar" ? s.destination.city_ar : s.destination.city_en}</span>
                    <span>${sub.toFixed(0)}</span>
                  </div>
                );
              })}
            </div>

            <button
              disabled={stops.length === 0}
              onClick={handleSave}
              data-testid="save-trip-btn"
              className="w-full h-12 rounded-full bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
            >
              <Save className="h-4 w-4" />
              {t("save_trip")}
            </button>

            <button
              disabled={stops.length === 0}
              onClick={() => window.print()}
              data-testid="print-trip-btn"
              className="w-full h-11 rounded-full border-2 border-white/20 hover:bg-white hover:text-[#1C1917] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium transition-colors"
            >
              <Printer className="h-4 w-4" />
              {L({ ar: "طباعة / حفظ PDF", en: "Print / Save PDF", fr: "Imprimer / PDF" })}
            </button>

            {shareUrl && (
              <div className="pt-4 border-t border-white/10 space-y-3" data-testid="trip-share-panel">
                <div className="text-[10px] uppercase tracking-widest opacity-70">
                  {L({ ar: "رابط المشاركة", en: "Share link", fr: "Lien de partage" })}
                </div>
                <div className="text-xs opacity-90 truncate bg-white/5 rounded-full px-3 py-2 border border-white/10">
                  {shareUrl}
                </div>
                <div className="bg-[#FAF7F2] text-[#1C1917] rounded-2xl p-3">
                  <SocialShare url={shareUrl} text={shareText} testidPrefix="trip-share" />
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function StopCard({ stop, onRemove, onUpdate }) {
  const { lang, t } = useI18n();
  const L = useL();
  const d = stop.destination;
  const p = d.prices;

  return (
    <div
      className="rounded-3xl bg-white border border-[#E6DED3] p-5 flex flex-col sm:flex-row gap-4"
      data-testid={`trip-stop-${d.id}`}
    >
      <img src={d.image_url} alt="" className="h-32 w-full sm:w-40 rounded-2xl object-cover" />
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#C85A32]">
              {t("day")} {stop.day}
            </div>
            <h3 className="font-display text-lg text-[#1C1917] mt-0.5">{lang === "ar" ? d.city_ar : d.city_en}</h3>
          </div>
          <button
            onClick={() => onRemove(d.id)}
            className="h-9 w-9 rounded-full hover:bg-[#F3EDE4] text-[#78716C] hover:text-[#C85A32] grid place-items-center transition-colors"
            data-testid={`remove-stop-${d.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <TierSelect
            icon={Hotel}
            label={t("hotel")}
            value={stop.hotel_tier}
            onChange={(v) => onUpdate(d.id, { hotel_tier: v })}
            prices={{ budget: p.hotel_budget_usd, mid: p.hotel_mid_usd, luxury: p.hotel_luxury_usd }}
          />
          <TierSelect
            icon={UtensilsCrossed}
            label={t("meal")}
            value={stop.meals_tier}
            onChange={(v) => onUpdate(d.id, { meals_tier: v })}
            prices={{ budget: p.meal_budget_usd, mid: p.meal_mid_usd, luxury: p.meal_luxury_usd }}
          />
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="text-xs text-[#78716C]">{L({ ar: "عدد الليالي", en: "Nights", fr: "Nuits" })}</div>
          <div className="flex items-center gap-2 border border-[#E6DED3] rounded-full">
            <button
              onClick={() => onUpdate(d.id, { nights: Math.max(1, stop.nights - 1) })}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-[#F3EDE4]"
              data-testid={`nights-dec-${d.id}`}
            >
              <Minus className="h-3 w-3" />
            </button>
            <span className="min-w-[24px] text-center font-medium">{stop.nights}</span>
            <button
              onClick={() => onUpdate(d.id, { nights: stop.nights + 1 })}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-[#F3EDE4]"
              data-testid={`nights-inc-${d.id}`}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TierSelect({ icon: Icon, label, value, onChange, prices }) {
  const tiers = ["budget", "mid", "luxury"];
  return (
    <div className="rounded-xl border border-[#E6DED3] p-2">
      <div className="flex items-center gap-1.5 text-[10px] text-[#78716C] uppercase tracking-widest">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="flex gap-1 mt-1.5">
        {tiers.map((tier) => (
          <button
            key={tier}
            onClick={() => onChange(tier)}
            className={`flex-1 px-1 py-1 rounded-md text-[11px] transition-colors ${
              value === tier ? "bg-[#1C1917] text-[#FAF7F2]" : "hover:bg-[#F3EDE4] text-[#57534E]"
            }`}
          >
            ${prices[tier]}
          </button>
        ))}
      </div>
    </div>
  );
}
